import { createFileRoute } from "@tanstack/react-router";
import { faceNew } from "geom/brep";
import { planeCopy, planeMoveAlongNormal } from "geom/plane";
import { splitFaceWithPlane } from "geom/polyclip/splitFaceWithPlane";
import { PlaneTrimTestCases } from "geom/polyclip/test/trimFaceWithPlaneTestCases";
import { trimFaceWithPlane } from "geom/polyclip/trimFaceWithPlane";
import { polyLine2SetCW } from "geom/polyLine2";
import { rect2ByCenterWidthHeight } from "geom/rect2";
import { defaultCameraSettings } from "gfx/CameraSettings";
import { debugPlaneToTHREE, faceToTHREE, polyLine2ToTHREE } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEViewer from "gfx/THREEViewer";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { DoubleSide, MeshBasicMaterial } from "three";
import colors from "ui/styles/colors";

function PlaneTrimTestPage() {
  const [viewer, setViewer] = useState<THREEViewer | null>(null);
  const viewerContainer = useRef<HTMLDivElement>(null);
  const [testIndex, setTestIndex] = useState(0);
  const [planeNudge, setPlaneNudge] = useState(8);
  const [showSubject, setShowSubject] = useState(true);
  const [fillResult, setFillResult] = useState(true);
  const [showCutPlane, setShowCutPlane] = useState(true);
  const [useSplit, setUseSplit] = useState(false);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [averageTime, setAverageTime] = useState<number>(0);

  useEffect(() => {
    const viewerInstance = new THREEViewer({
      container: viewerContainer.current as HTMLDivElement,
      options: {
        gui: false,
        shadows: true,
        animate: true,
        showAxes: false,
        ssfxRenderer: true,
        debugFeaturesEnabled: false,
        sceneLights: envPresetSceneLights[EnvPreset.WHITEBOX_LOW_FOG],
        cameraSettings: {
          ...defaultCameraSettings,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
          x: 500,
          y: -500,
          z: 500,
        },
      },
    });
    viewerInstance.controls.maxPolarAngle = Math.PI;
    viewerInstance.controls.update();

    setViewer(viewerInstance);

    return () => {
      viewerInstance.dispose();
      setViewer(null);
    };
  }, []);

  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const cutPlane = planeMoveAlongNormal(planeCopy(PlaneTrimTestCases[testIndex].cutPlane), planeNudge);

    const c = PlaneTrimTestCases[testIndex];

    if (showSubject) {
      for (const face of c.faces) {
        for (const loop of face.loops) {
          viewer.add(polyLine2ToTHREE({ path: loop, plane: face.plane }));
        }
      }
    }

    if (showCutPlane) {
      viewer.add(
        faceToTHREE(
          faceNew([polyLine2SetCW(rect2ByCenterWidthHeight(0, 0, 200, 200))], cutPlane),
          new MeshBasicMaterial({
            color: colors.blue,
            transparent: true,
            opacity: 0.2,
            side: DoubleSide,
          }),
        ),
      );

      viewer.add(debugPlaneToTHREE(cutPlane));
    }

    const count = 100;
    const t0 = performance.now();
    for (let i = 0; i < count; i++) {
      for (const face of c.faces) {
        if (useSplit) {
          splitFaceWithPlane(face, cutPlane);
        } else {
          trimFaceWithPlane(face, cutPlane);
        }
      }
    }
    const t1 = performance.now();
    const total = t1 - t0;
    const average = total / count;

    setTotalTime(total);
    setAverageTime(average);

    for (const face of c.faces) {
      if (useSplit) {
        const res = splitFaceWithPlane(face, cutPlane);

        // Render back faces in green
        for (const face of res.backFaces) {
          if (fillResult) {
            viewer.add(
              faceToTHREE(
                face,
                new MeshBasicMaterial({
                  color: colors.green,
                  transparent: true,
                  opacity: 1,
                  side: DoubleSide,
                }),
              ),
            );
          }

          for (const loop of face.loops) {
            const r = polyLine2ToTHREE({ path: loop, plane: face.plane });
            viewer.add(r);
          }
        }

        // Render front faces in red
        for (const face of res.frontFaces) {
          if (fillResult) {
            viewer.add(
              faceToTHREE(
                face,
                new MeshBasicMaterial({
                  color: colors.red,
                  transparent: true,
                  opacity: 1,
                  side: DoubleSide,
                }),
              ),
            );
          }

          for (const loop of face.loops) {
            const r = polyLine2ToTHREE({ path: loop, plane: face.plane });
            viewer.add(r);
          }
        }
      } else {
        const res = trimFaceWithPlane(face, cutPlane);

        for (const face of res.faces ?? []) {
          if (fillResult) {
            viewer.add(
              faceToTHREE(
                face,
                new MeshBasicMaterial({
                  color: colors.green,
                  transparent: true,
                  opacity: 1,
                  side: DoubleSide,
                }),
              ),
            );
          }

          for (const loop of face.loops) {
            const r = polyLine2ToTHREE({ path: loop, plane: face.plane });
            r.renderOrder = 4;
            viewer.add(r);
          }
        }
      }
    }

    return () => {
      viewer.scene.clear();
    };
  }, [viewer, testIndex, planeNudge, showSubject, fillResult, showCutPlane, useSplit]);

  return (
    <>
      <Head>
        <style>{`
          fieldset div {
            height: 2em;
            display: flex;
            flex-direction: row;
            align-items: center;
          }

          fieldset div label {
            width: 8em;
            text-align: right;
            padding-right: 1em;
          }

          .performance-metrics {
            margin-top: 1em;
            padding-top: 1em;
            border-top: 1px solid #ccc;
          }

          .performance-metrics div {
            color: #666;
          }
        `}</style>
      </Head>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }}
        ref={viewerContainer}
      >
        <fieldset style={{ display: "flex", flexDirection: "column" }}>
          <div>
            <label htmlFor="testCase">Test case</label>
            <select
              name="testCase"
              value={testIndex}
              onChange={(x) => {
                setTestIndex(parseInt(x.target.value));
                setPlaneNudge(0);
              }}
            >
              {PlaneTrimTestCases.map((c, i) => (
                <option key={i} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="testCase">Plane nudge</label>
            <input
              name="planeNudge"
              type="range"
              step={1}
              min={-200}
              max={200}
              value={planeNudge}
              onChange={(x) => setPlaneNudge(parseFloat(x.target.value))}
            />
            <input
              style={{ width: "4em", marginLeft: "0.5em", padding: 0, textAlign: "center" }}
              type="number"
              value={planeNudge}
              onChange={(x) => setPlaneNudge(parseFloat(x.target.value))}
            ></input>
          </div>

          <div>
            <label htmlFor="showSubject">Show subject</label>
            <input
              name="showSubject"
              type="checkbox"
              checked={showSubject}
              onChange={() => setShowSubject(!showSubject)}
            />
          </div>

          <div>
            <label htmlFor="showCutPlane">Show cut plane</label>
            <input
              name="showCutPlane"
              type="checkbox"
              checked={showCutPlane}
              onChange={() => setShowCutPlane(!showCutPlane)}
            />
          </div>

          <div>
            <label htmlFor="fillResult">Fill result</label>
            <input name="fillResult" type="checkbox" checked={fillResult} onChange={() => setFillResult(!fillResult)} />
          </div>

          <div>
            <label htmlFor="useSplit">Use split</label>
            <input name="useSplit" type="checkbox" checked={useSplit} onChange={() => setUseSplit(!useSplit)} />
          </div>

          <div className="performance-metrics">
            <div>
              <label>Total time (100x):</label>
              <span>{totalTime.toFixed(2)} ms</span>
            </div>
            <div>
              <label>Average time:</label>
              <span>{averageTime.toFixed(2)} ms</span>
            </div>
          </div>
        </fieldset>
      </div>
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        ref={viewerContainer}
      />
    </>
  );
}

export const Route = createFileRoute("/debug/polyclip/plane-trim-test")({
  component: PlaneTrimTestPage,
});
